import { AccessLevel, Section as SectionType } from 'app/frontend/types/custom'
import { PropsWithChildren } from 'react'
import AuthContext from '../config/context/auth'
import { useGuild } from '../config/context/guild'
import { accessLevels } from 'app/frontend/types/custom/AccessLevel'
import UnauthorizedInfo from './UnauthorizedInfo'

interface Props {
  alwaysRenderContent?: boolean
  requirement?: AccessLevel
  requireAllTeams?: boolean
  renderUnauthorized?: boolean
  section: SectionType
  teamReference?: wowaudit.TeamReference
}

export const hasAccessToContent = (
  requirement: AccessLevel,
  section: SectionType,
  guild?: wowaudit.Guild,
  teamReference?: wowaudit.TeamReference,
  requireAllTeams = false,
) => {
  const accessLevelForTeam = (teamId: number | string | undefined) => {
    return teamId
      ? guild?.teamAccessLevels[teamId.toString()]?.[section] ?? 'none'
      : 'none'
  }

  const access = requireAllTeams
    ? Object.keys(guild?.teamAccessLevels ?? {}).map((id) =>
        accessLevelForTeam(id),
      )
    : [accessLevelForTeam(teamReference?.id)]

  const accessLevel =
    section === 'none'
      ? 'admin'
      : access.reduce(
          (highest, current) =>
            accessLevels.indexOf(current) > accessLevels.indexOf(highest)
              ? current
              : highest,
          'admin',
        )

  const hasAccess =
    accessLevels.indexOf(accessLevel) <= accessLevels.indexOf(requirement)

  return { accessLevel, hasAccess }
}

const ProtectedContent = ({
  alwaysRenderContent = false,
  children,
  section,
  requirement = 'member',
  requireAllTeams = false,
  renderUnauthorized = true,
  teamReference,
}: PropsWithChildren<Props>) => {
  const { guild, teamReference: teamRefFromContext } = useGuild()

  const { accessLevel, hasAccess } = hasAccessToContent(
    requirement,
    section,
    guild,
    teamRefFromContext ?? teamReference,
    requireAllTeams,
  )

  if (!hasAccess && !alwaysRenderContent) {
    return renderUnauthorized ? (
      <AuthContext.Provider value={{ accessLevel, section, requirement }}>
        <UnauthorizedInfo teamReference={teamRefFromContext ?? teamReference} />
      </AuthContext.Provider>
    ) : null
  }

  return (
    <AuthContext.Provider value={{ accessLevel, section, requirement }}>
      {children}
    </AuthContext.Provider>
  )
}

export default ProtectedContent
